import { productionOptimizedService } from './productionOptimizedService';
import { normalizeOrganizationId } from '../utils/organizationAutoHeal';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Authentication-Aware Persistence Service
 * Handles data loading based on authentication status
 */
export class AuthAwarePersistenceService {
  constructor() {
    this.isAuthenticated = false;
    this.hasLoaded = false;
  }

  // Set authentication status
  setAuthenticationStatus(isAuthenticated) {
    this.isAuthenticated = isAuthenticated;
    console.log(`🔐 Authentication status: ${isAuthenticated ? 'Authenticated' : 'Not authenticated'}`);
  }

  // Initialize data based on authentication
  async initialize(isAuthenticated) {
    this.setAuthenticationStatus(isAuthenticated);
    
    if (isAuthenticated && !this.hasLoaded) {
      // Load fresh data from Firebase
      console.log('🔄 Loading fresh data from Firebase...');
      try {
        const data = await productionOptimizedService.initializeUltraFast(true);
        this.hasLoaded = true;
        return data;
      } catch (error) {
        console.error('❌ Error loading authenticated data:', error);
        // Fall back to cached data
        return this.loadFromCache();
      }
    } else {
      // Load from cache or default
      return this.loadFromCache();
    }
  }

  /**
   * Load org-scoped clinical structure for a specific organization.
   * Uses clinicalStructureService.getStructureByOrganization (org-filtered hierarchical queries)
   * plus biologicalEntityService.getAll (global reference data, no org scope needed).
   *
   * Replaces productionOptimizedService.loadMinimalEssential() which called FirebaseService.getAll()
   * on 11 collections with no organizationId filter — cross-tenant data exposure risk.
   *
   * @param {string} organizationId
   * @returns {Promise<object>} structure object compatible with ManagementDashboard's `structure` prop
   */
  async loadForOrg(organizationId) {
    const orgId = normalizeOrganizationId(organizationId);
    if (!orgId) {
      console.warn('[AuthAwarePersistenceService] loadForOrg called without organizationId');
      return this.getEmptyStructure();
    }

    try {
      console.log(`🔄 [AuthAwarePersistenceService] Loading org structure for: ${orgId}`);

      const { clinicalStructureService, biologicalEntityService } = await import('./firebaseService');

      const [clinicalStructure, biological_entities, orgSnap] = await Promise.all([
        clinicalStructureService.getStructureByOrganization(orgId),
        biologicalEntityService.getAll(),
        getDoc(doc(db, 'organizations', orgId))
      ]);

      const orgRecord = orgSnap.exists()
        ? { id: orgId, ...orgSnap.data() }
        : { id: orgId };

      console.log(`✅ [AuthAwarePersistenceService] Org structure loaded for: ${orgId}`);

      return {
        organizations: [orgRecord],
        company: null,
        // Org-scoped clinical structure (locations → buildings → floors → departments → rooms → beds)
        locations: clinicalStructure.locations || [],
        buildings: clinicalStructure.buildings || [],
        floors: clinicalStructure.floors || [],
        departments: clinicalStructure.departments || [],
        rooms: clinicalStructure.rooms || [],
        beds: clinicalStructure.beds || [],
        equipment: clinicalStructure.equipment || [],
        personnel: clinicalStructure.personnel || [],
        // Global reference data — biological entities are not org-scoped (standard medical taxonomy)
        biological_entities: biological_entities || [],
        // Users loaded per-component via useOrgUsers hook; not needed at app level
        users: []
      };
    } catch (error) {
      console.error('[AuthAwarePersistenceService] loadForOrg failed:', error);
      return this.getEmptyStructure();
    }
  }

  /**
   * Empty structure compatible with ManagementDashboard's `structure` prop.
   * Used as fallback when org is not resolved or loadForOrg fails.
   */
  getEmptyStructure() {
    return {
      organizations: [],
      company: null,
      locations: [],
      buildings: [],
      floors: [],
      departments: [],
      rooms: [],
      beds: [],
      equipment: [],
      personnel: [],
      biological_entities: [],
      users: []
    };
  }

  // Load from cache or default structure
  loadFromCache() {
    console.log('📦 Loading from cache or default structure...');
    try {
      // Try to load from localStorage cache
      const cachedData = productionOptimizedService.loadFromLocalStorage();
      if (cachedData && productionOptimizedService.isCacheValid(cachedData)) {
        console.log('✅ Loaded from cache');
        return cachedData;
      }
      
      // Fall back to default structure
      console.log('🔄 Using default structure');
      return productionOptimizedService.getDefaultStructure();
    } catch (error) {
      console.error('❌ Error loading from cache:', error);
      return productionOptimizedService.getDefaultStructure();
    }
  }

  // Save data (only if authenticated)
  stampStructureOrganizationIds(structure, organizationId) {
    const orgId = normalizeOrganizationId(organizationId);
    if (!orgId || !structure) return structure;

    const locationOrg = (loc) => normalizeOrganizationId(loc.organizationId) || orgId;
    const locations = (structure.locations || []).map((loc) => ({
      ...loc,
      organizationId: locationOrg(loc)
    }));
    const locationOrgById = Object.fromEntries(locations.map((l) => [l.id, l.organizationId]));

    return {
      ...structure,
      locations,
      buildings: (structure.buildings || []).map((b) => ({
        ...b,
        organizationId: normalizeOrganizationId(b.organizationId) || locationOrgById[b.locationId] || orgId
      }))
    };
  }

  async saveData(structure, organizationId = null) {
    if (!this.isAuthenticated) {
      console.log('⚠️ Not authenticated, saving to local cache only');
      productionOptimizedService.saveToLocalStorage(structure);
      return;
    }

    const stampedStructure = this.stampStructureOrganizationIds(structure, organizationId);
    const failedCollections = [];

    try {
      const structural = [
        ['organizations', stampedStructure.organizations],
        ['locations', stampedStructure.locations],
        ['buildings', stampedStructure.buildings],
        ['floors', stampedStructure.floors],
        ['departments', stampedStructure.departments],
        ['rooms', stampedStructure.rooms],
        ['beds', stampedStructure.beds]
      ];
      for (const [name, items] of structural) {
        try {
          await productionOptimizedService.saveWithOfflineSupport(name, items || [], { throwOnError: true });
        } catch (error) {
          failedCollections.push(name);
          console.error(`❌ Failed to save ${name}:`, error);
        }
      }
      for (const [name, items] of [
        ['equipment', stampedStructure.equipment],
        ['personnel', stampedStructure.personnel],
        ['biological_entities', stampedStructure.biological_entities]
      ]) {
        try {
          await productionOptimizedService.saveWithOfflineSupport(name, items || [], { throwOnError: true });
        } catch (error) {
          failedCollections.push(name);
          console.error(`❌ Failed to save ${name}:`, error);
        }
      }

      if (failedCollections.length > 0) {
        throw new Error(`Firestore save failed for: ${failedCollections.join(', ')}`);
      }
      console.log('💾 All data saved to Firebase');
    } catch (error) {
      console.error('❌ Error saving to Firebase:', error);
      productionOptimizedService.saveToLocalStorage(stampedStructure);
      throw error;
    }
  }

  // Load additional data on demand
  async loadOnDemand(collectionType) {
    if (!this.isAuthenticated) {
      console.log(`⚠️ Not authenticated, cannot load ${collectionType} from Firebase`);
      return [];
    }

    return await productionOptimizedService.loadOnDemand(collectionType);
  }

  // Clear cache
  clearCache() {
    productionOptimizedService.clearAllCaches();
    this.hasLoaded = false;
  }
}

// Export singleton instance
export const authAwarePersistenceService = new AuthAwarePersistenceService();
